import { BRAND, BRAND_LONG } from "@/lib/brand";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { CalendarPlus, MapPin, Phone, ArrowRight, Stethoscope } from "lucide-react";
import { toast } from "sonner";

export default function BookingHome() {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/hospitals");
        setHospitals(data || []);
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail || e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="booking-home">
      <header className="bg-gradient-to-r from-blue-800 to-cyan-500 text-white">
        <div className="max-w-5xl mx-auto px-6 py-10 md:py-14">
          <Link to="/login" className="text-xs tracking-[0.32em] text-white/80 hover:text-white">{BRAND}</Link>
          <h1 className="text-3xl md:text-5xl font-extrabold mt-3" style={{ fontFamily: "Work Sans" }}>Set Appointment</h1>
          <p className="text-white/85 mt-2 md:text-lg">Choose a clinic to begin. Pay upfront via QR. Your slot is held once payment is verified.</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Available clinics</h2>

        {loading ? (
          <div className="text-slate-500">Loading clinics…</div>
        ) : hospitals.length === 0 ? (
          <div className="p-6 bg-white border border-slate-200 rounded-2xl text-slate-500">
            No clinics currently accept online appointments. Please contact the clinic directly.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="hospital-list">
            {hospitals.map((h) => (
              <Link
                key={h.id}
                to={`/book/${h.slug || h.id}`}
                className="group bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md hover:border-blue-300 transition flex items-start gap-4"
                data-testid={`hospital-card-${h.id}`}
              >
                <div className="h-12 w-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                  <Stethoscope size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 group-hover:text-blue-700 transition">{h.hospital_name}</div>
                  {h.address ? (
                    <div className="text-sm text-slate-500 mt-1 flex items-start gap-1"><MapPin size={14} className="mt-0.5" />{h.address}</div>
                  ) : null}
                  {h.contact_number ? (
                    <div className="text-sm text-slate-500 mt-1 flex items-center gap-1"><Phone size={14} />{h.contact_number}</div>
                  ) : null}
                </div>
                <ArrowRight size={20} className="text-slate-400 group-hover:text-blue-700 mt-1" />
              </Link>
            ))}
          </div>
        )}

        <div className="mt-10 text-center text-sm text-slate-500">
          Already have an account? <Link to="/login" className="text-blue-700 font-medium hover:underline">Sign in</Link>
        </div>
      </main>

      <div className="fixed top-4 right-4 md:hidden bg-blue-700 text-white p-2.5 rounded-full shadow-lg">
        <CalendarPlus size={18} />
      </div>
    </div>
  );
}
